import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SupplierFullDtoIn } from '@/models/in/SupplierFullDtoIn';
import { supplierOptionLabel } from './existingSupplier';

interface ExistingSupplierPickerProps {
  /** The fund's suppliers (GET funds/{fundId}/suppliers). */
  suppliers: SupplierFullDtoIn[];
  onPick: (supplier: SupplierFullDtoIn) => void;
}

/**
 * "בחירה מרשימת הספקים" (SPR-10302): a search box over the suppliers the fund already works with,
 * by name or tax id. Choosing one fills the supplier fields below it; typing a known tax id in the
 * tax-id field does the same, so the coordinator can use whichever they have at hand.
 *
 * A native datalist rather than a custom combobox: it is keyboard- and screen-reader-friendly out of
 * the box, and the list is one fund's suppliers, not thousands.
 */
const ExistingSupplierPicker = ({ suppliers, onPick }: ExistingSupplierPickerProps) => {
  const { t } = useTranslation();
  const id = useId();
  const [query, setQuery] = useState('');

  if (suppliers.length === 0) return null;

  const handleChange = (value: string) => {
    setQuery(value);
    const picked = suppliers.find((supplier) => supplierOptionLabel(supplier) === value);
    if (picked) {
      onPick(picked);
      setQuery('');
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={`${id}-input`} className="body-sm-semibold text-(--font-secondary)">
        {t('projects.newSupplier.pickExistingLabel')}
      </label>
      <input
        id={`${id}-input`}
        type="search"
        list={`${id}-list`}
        autoComplete="off"
        className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
        placeholder={t('projects.newSupplier.pickExistingPlaceholder')}
        value={query}
        onChange={(e) => handleChange(e.target.value)}
      />
      <datalist id={`${id}-list`}>
        {suppliers.map((supplier) => (
          <option key={supplier.id} value={supplierOptionLabel(supplier)} />
        ))}
      </datalist>
      <p className="body-xs-regular text-(--font-secondary)">{t('projects.newSupplier.pickExistingHint')}</p>
    </div>
  );
};

export default ExistingSupplierPicker;
